import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import campaignService, {
  CampaignBulkRecipient,
  CampaignBulkStatusResponse,
  CampaignWebhookEvent,
  CampaignWebhookConfigUpdateRequest,
} from '../../services/campaignService';
import projectService, { ProjectMetadata } from '../../services/projectService';
import { VOICE_MODEL_OPTIONS } from '../../constants/clientVoice';
import { useAuth } from '../../contexts/AuthContext';

type LiveStatus = 'idle' | 'connecting' | 'connected' | 'fallback-polling' | 'disconnected';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'stopped']);
const DEFAULT_REENGAGEMENT_DELAY_DAYS = 4;
const MIN_REENGAGEMENT_DELAY_DAYS = 0.000001;
const DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS = 2;
const DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS = 1;

type WebhookConfigDraft = {
  projectInterest: string;
  voiceModelSelection: string;
  skipIdentityValidation: boolean;
  scheduleReengagement: boolean;
  reengagementDelayBusinessDays: number;
  noAnswerReengagementAttempts: number;
  noAnswerReengagementIncrementBusinessDays: number;
  headerMediaType: 'image' | 'video' | null;
  headerMediaId: string;
  headerMediaUrl: string;
  headerImageMediaId: string;
  headerImageUrl: string;
};

const normalizeReengagementDelayInput = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : MIN_REENGAGEMENT_DELAY_DAYS;
};

const normalizeNoAnswerAttemptsInput = (value: string): number => {
  const parsed = Math.trunc(Number(value));
  return Number.isFinite(parsed) ? Math.min(10, Math.max(1, parsed)) : 1;
};

const getProjectOptionLabel = (project: ProjectMetadata): string =>
  project.project_name || project.project_code;

const buildWebhookConfigDraft = (status: CampaignBulkStatusResponse): WebhookConfigDraft => ({
  projectInterest: status.project_interest || '',
  voiceModelSelection: status.voice_model_selection || '',
  skipIdentityValidation: Boolean(status.skip_identity_validation),
  scheduleReengagement: status.schedule_reengagement !== false,
  reengagementDelayBusinessDays:
    typeof status.reengagement_delay_business_days === 'number' && status.reengagement_delay_business_days > 0
      ? status.reengagement_delay_business_days
      : DEFAULT_REENGAGEMENT_DELAY_DAYS,
  noAnswerReengagementAttempts:
    typeof status.no_answer_reengagement_attempts === 'number'
      ? status.no_answer_reengagement_attempts
      : DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS,
  noAnswerReengagementIncrementBusinessDays:
    typeof status.no_answer_reengagement_increment_business_days === 'number' &&
    status.no_answer_reengagement_increment_business_days > 0
      ? status.no_answer_reengagement_increment_business_days
      : DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS,
  headerMediaType: status.header_media_type || null,
  headerMediaId: status.header_media_id || status.header_image_media_id || '',
  headerMediaUrl: status.header_media_url || status.header_image_url || '',
  headerImageMediaId: status.header_image_media_id || '',
  headerImageUrl: status.header_image_url || '',
});

const getWebhookConfigSignature = (status: CampaignBulkStatusResponse): string =>
  JSON.stringify(buildWebhookConfigDraft(status));

const humanizeDeliveryError = (errorCode?: number | null, fallback?: string | null): string => {
  switch (errorCode) {
    case 131049:
      return 'Buzón del cliente saturado';
    case 131026:
      return 'Número inactivo o bloqueado';
    case 131050:
      return 'Cliente rechazó recibir marketing';
    case 130429:
      return 'Límite de envíos alcanzado';
    case 130472:
      return 'Restricción temporal de WhatsApp';
    default:
      return fallback || 'No se pudo entregar';
  }
};

type CampaignDeliveryStatusLike = {
  status?: string | null;
  wa_status?: string | null;
  wa_error_code?: number | null;
  wa_error_description?: string | null;
  error_message?: string | null;
  retry_count?: number | null;
  max_retries?: number | null;
};

export const getRecipientDetailText = (
  recipient: CampaignDeliveryStatusLike
): string => {
  const normalized = String(recipient.wa_status || recipient.status || '').toLowerCase();
  if (!['failed', 'retry_exhausted', 'retry_scheduled'].includes(normalized)) {
    return '';
  }

  const fallback =
    String(recipient.wa_error_description || recipient.error_message || '').trim() || null;
  if (!recipient.wa_error_code && !fallback && normalized !== 'retry_exhausted') {
    return '';
  }

  return humanizeDeliveryError(recipient.wa_error_code, fallback);
};

const getRecipientStatusPresentation = (
  recipient: CampaignDeliveryStatusLike
): { color: 'default' | 'info' | 'success' | 'warning' | 'error'; label: string } => {
  const normalized = String(recipient.wa_status || recipient.status || '').toLowerCase();

  if (normalized === 'processing') {
    return { color: 'default', label: 'Procesando envío' };
  }
  if (normalized === 'sent') {
    return { color: 'info', label: 'Enviado' };
  }
  if (normalized === 'delivered') {
    return { color: 'success', label: 'Recibido por el cliente' };
  }
  if (normalized === 'read') {
    return { color: 'success', label: 'Leído por el cliente' };
  }
  if (normalized === 'retry_scheduled') {
    const retryCount = recipient.retry_count || 0;
    const maxRetries = recipient.max_retries || 0;
    const suffix = maxRetries ? ` (${retryCount}/${maxRetries})` : '';
    return { color: 'warning', label: `Reintentando envío${suffix}` };
  }
  if (normalized === 'retry_exhausted') {
    return { color: 'error', label: 'No se pudo entregar' };
  }
  if (normalized === 'failed') {
    if (recipient.wa_error_code === 131049) {
      return { color: 'warning', label: 'Buzón del cliente saturado' };
    }
    if (recipient.wa_error_code === 130429) {
      return { color: 'warning', label: 'Límite de envíos alcanzado' };
    }
    if (recipient.wa_error_code === 131026) {
      return { color: 'error', label: 'Número inactivo o bloqueado' };
    }
    if (recipient.wa_error_code === 131050) {
      return { color: 'error', label: 'Cliente rechazó recibir marketing' };
    }
    if (recipient.wa_error_code === 130472) {
      return { color: 'error', label: 'Restricción temporal de WhatsApp' };
    }
    return { color: 'error', label: 'Fallido' };
  }
  if (normalized === 'pending') {
    return { color: 'warning', label: 'Pendiente' };
  }
  if (normalized === 'invalid') {
    return { color: 'default', label: 'Inválido' };
  }
  return { color: 'default', label: recipient.status || 'Desconocido' };
};

const ProgressRow: React.FC<{
  label: string;
  value: number;
  total: number;
  color: string;
}> = ({ label, value, total, color }) => {
  const percent = total > 0 ? (value / total) * 100 : 0;
  return (
    <Stack spacing={0.6}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {value} · {percent.toFixed(1)}%
        </Typography>
      </Stack>
      <Box
        sx={{
          height: 10,
          borderRadius: 999,
          overflow: 'hidden',
          bgcolor: 'rgba(17, 24, 39, 0.08)',
        }}
      >
        <Box
          sx={{
            width: `${Math.max(0, Math.min(100, percent))}%`,
            height: '100%',
            bgcolor: color,
          }}
        />
      </Box>
    </Stack>
  );
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('es-CO');
};

const CampaignStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'completed') {
    return <Chip size="small" color="success" label="Completada" />;
  }
  if (normalized === 'failed') {
    return <Chip size="small" color="error" label="Fallida" />;
  }
  if (normalized === 'running') {
    return <Chip size="small" color="primary" label="En ejecución" />;
  }
  if (normalized === 'queued') {
    return <Chip size="small" color="warning" label="En cola" />;
  }
  if (normalized === 'cancelled') {
    return <Chip size="small" color="default" label="Cancelada" />;
  }
  if (normalized === 'live') {
    return <Chip size="small" color="success" label="En vivo" />;
  }
  if (normalized === 'stopped') {
    return <Chip size="small" color="default" label="Detenida" />;
  }
  return <Chip size="small" label={status || 'desconocido'} />;
};

const DeliveryStatusChip: React.FC<{ item: CampaignDeliveryStatusLike }> = ({ item }) => {
  const presentation = getRecipientStatusPresentation(item);
  return <Chip size="small" color={presentation.color} label={presentation.label} />;
};

const CampaignHistory: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignBulkStatusResponse[]>([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignPage, setCampaignPage] = useState(0);
  const [campaignPageSize, setCampaignPageSize] = useState(25);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [selectedCampaignStatus, setSelectedCampaignStatus] = useState<CampaignBulkStatusResponse | null>(null);
  const [recipients, setRecipients] = useState<CampaignBulkRecipient[]>([]);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState<CampaignWebhookEvent[]>([]);
  const [webhookEventsTotal, setWebhookEventsTotal] = useState(0);
  const [webhookEventsLoading, setWebhookEventsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [webhookConfigDraft, setWebhookConfigDraft] = useState<WebhookConfigDraft | null>(null);
  const [webhookConfigDraftCampaignId, setWebhookConfigDraftCampaignId] = useState<string | null>(null);
  const [webhookConfigAppliedSignature, setWebhookConfigAppliedSignature] = useState<string | null>(null);
  const [webhookConfigDirty, setWebhookConfigDirty] = useState(false);
  const [webhookConfigSaving, setWebhookConfigSaving] = useState(false);
  const [webhookConfigUploadingMedia, setWebhookConfigUploadingMedia] = useState(false);
  const [webhookConfigSuccess, setWebhookConfigSuccess] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<ProjectMetadata[]>([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [retryingRecipientId, setRetryingRecipientId] = useState<number | null>(null);
  const [retryingWebhookEventId, setRetryingWebhookEventId] = useState<number | null>(null);
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientPageSize, setRecipientPageSize] = useState(25);
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('all');

  const [listError, setListError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<LiveStatus>('idle');

  const pollTimerRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const recipientPageRef = useRef(0);
  const recipientPageSizeRef = useRef(25);
  const recipientStatusFilterRef = useRef('all');

  useEffect(() => {
    recipientPageRef.current = recipientPage;
  }, [recipientPage]);

  useEffect(() => {
    recipientPageSizeRef.current = recipientPageSize;
  }, [recipientPageSize]);

  useEffect(() => {
    recipientStatusFilterRef.current = recipientStatusFilter;
  }, [recipientStatusFilter]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const campaignId = (searchParams.get('campaignId') || '').trim();
    if (!campaignId) {
      return;
    }
    setSelectedCampaignId(campaignId);
    setSelectedCampaignStatus((current) => (current?.campaign_id === campaignId ? current : null));
  }, [searchParams]);

  const selectedCampaignListItem = useMemo(
    () => campaigns.find((item) => item.campaign_id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId]
  );

  const campaignDetail = useMemo(() => {
    if (selectedCampaignStatus?.campaign_id === selectedCampaignId) {
      return selectedCampaignStatus;
    }
    return selectedCampaignListItem;
  }, [selectedCampaignId, selectedCampaignListItem, selectedCampaignStatus]);

  const selectedCampaignType = campaignDetail?.campaign_type || null;
  const selectedWebhookProject = useMemo(() => {
    const value = (webhookConfigDraft?.projectInterest || '').trim().toLocaleLowerCase();
    if (!value) return null;
    return projectOptions.find(
      (project) =>
        project.project_code.toLocaleLowerCase() === value ||
        project.project_name.toLocaleLowerCase() === value
    ) || null;
  }, [projectOptions, webhookConfigDraft?.projectInterest]);

  useEffect(() => {
    if (!campaignDetail || (campaignDetail.campaign_type || 'bulk') !== 'webhook') {
      setWebhookConfigDraft(null);
      setWebhookConfigDraftCampaignId(null);
      setWebhookConfigAppliedSignature(null);
      setWebhookConfigDirty(false);
      setWebhookConfigSuccess(null);
      return;
    }

    const nextSignature = getWebhookConfigSignature(campaignDetail);
    const shouldHydrateDraft =
      webhookConfigDraftCampaignId !== campaignDetail.campaign_id ||
      (!webhookConfigDirty && webhookConfigAppliedSignature !== nextSignature);

    if (!shouldHydrateDraft) {
      return;
    }

    const nextDraft = buildWebhookConfigDraft(campaignDetail);
    setWebhookConfigDraft(nextDraft);
    // Search the canonical catalog by the persisted code/name so an existing
    // selection remains visible even when it is outside the first page.
    setProjectSearchInput(nextDraft.projectInterest);
    setWebhookConfigDraftCampaignId(campaignDetail.campaign_id);
    setWebhookConfigAppliedSignature(nextSignature);
    setWebhookConfigDirty(false);
    setWebhookConfigSuccess(null);
  }, [campaignDetail, webhookConfigAppliedSignature, webhookConfigDirty, webhookConfigDraftCampaignId]);

  useEffect(() => {
    if (selectedCampaignType !== 'webhook') {
      return;
    }

    let isMounted = true;
    setProjectsLoading(true);
    projectService
      .getProjects(0, 25, true, projectSearchInput.trim() || undefined)
      .then((response) => {
        if (isMounted) {
          setProjectOptions(response.projects || []);
        }
      })
      .catch((error) => {
        console.error('Error loading campaign projects:', error);
      })
      .finally(() => {
        if (isMounted) {
          setProjectsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCampaignType, projectSearchInput]);

  const clearProgressTracking = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const syncCampaignInList = (campaignStatus: CampaignBulkStatusResponse) => {
    setCampaigns((current) =>
      current.map((item) => (item.campaign_id === campaignStatus.campaign_id ? campaignStatus : item))
    );
  };

  const loadCampaigns = async () => {
    setCampaignsLoading(true);
    setListError(null);
    try {
      const response = await campaignService.listCampaigns({
        status: campaignStatusFilter === 'all' ? undefined : campaignStatusFilter,
        skip: campaignPage * campaignPageSize,
        limit: campaignPageSize,
      });
      setCampaigns(response.campaigns || []);
      setCampaignsTotal(response.total_count || 0);

      if (selectedCampaignId && !response.campaigns.some((item) => item.campaign_id === selectedCampaignId)) {
        // Keep selected detail visible even if filtered/paginated out from the list.
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar el historial de campañas.';
      setListError(String(backendMessage));
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchSelectedCampaignStatus = async (campaignId: string): Promise<CampaignBulkStatusResponse | null> => {
    try {
      const status = await campaignService.getCampaignStatus(campaignId);
      setSelectedCampaignStatus(status);
      syncCampaignInList(status);
      return status;
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible consultar el estado de la campaña.';
      setDetailError(String(backendMessage));
      return null;
    }
  };

  const fetchWebhookEvents = async (campaignId: string, page: number, pageSize: number) => {
    setWebhookEventsLoading(true);
    try {
      const response = await campaignService.getWebhookCampaignEvents(campaignId, {
        skip: page * pageSize,
        limit: pageSize,
      });
      setWebhookEvents(response.events || []);
      setWebhookEventsTotal(response.total_count || 0);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar eventos del webhook.';
      setDetailError(String(backendMessage));
    } finally {
      setWebhookEventsLoading(false);
    }
  };

  const fetchRecipients = async (campaignId: string, page: number, pageSize: number, status: string) => {
    setRecipientsLoading(true);
    try {
      const response = await campaignService.getBulkCampaignRecipients(campaignId, {
        skip: page * pageSize,
        limit: pageSize,
        status: status === 'all' ? undefined : status,
      });
      setRecipients(response.recipients || []);
      setRecipientsTotal(response.total_count || 0);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar destinatarios de la campaña.';
      setDetailError(String(backendMessage));
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleExportRecipients = async () => {
    if (!selectedCampaignId) {
      return;
    }

    setExportLoading(true);
    setDetailError(null);
    try {
      const blob = await campaignService.exportBulkCampaignRecipients(selectedCampaignId, {
        status: recipientStatusFilter === 'all' ? undefined : recipientStatusFilter,
      });
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const safeCampaignId = selectedCampaignId.replace(/[^a-zA-Z0-9_-]+/g, '_');
      downloadBlob(blob, `campaign_${safeCampaignId}_details_${timestamp}.xlsx`);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible exportar el detalle de destinatarios.';
      setDetailError(String(backendMessage));
    } finally {
      setExportLoading(false);
    }
  };

  const handleRetryRecipient = async (recipient: CampaignBulkRecipient) => {
    if (!selectedCampaignId) {
      return;
    }

    setDetailError(null);
    setRetryingRecipientId(recipient.id);
    try {
      await campaignService.retryBulkCampaignRecipient(selectedCampaignId, recipient.id);
      await fetchSelectedCampaignStatus(selectedCampaignId);
      await fetchRecipients(
        selectedCampaignId,
        recipientPageRef.current,
        recipientPageSizeRef.current,
        recipientStatusFilterRef.current
      );
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible reintentar el destinatario.';
      setDetailError(String(backendMessage));
    } finally {
      setRetryingRecipientId(null);
    }
  };

  const handleRetryWebhookEvent = async (eventRow: CampaignWebhookEvent) => {
    if (!selectedCampaignId) {
      return;
    }

    setDetailError(null);
    setRetryingWebhookEventId(eventRow.id);
    try {
      await campaignService.retryWebhookCampaignEvent(selectedCampaignId, eventRow.id);
      await fetchSelectedCampaignStatus(selectedCampaignId);
      await fetchWebhookEvents(
        selectedCampaignId,
        recipientPageRef.current,
        recipientPageSizeRef.current
      );
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible reintentar el evento del webhook.';
      setDetailError(String(backendMessage));
    } finally {
      setRetryingWebhookEventId(null);
    }
  };

  const handleStopCampaign = async () => {
    if (!selectedCampaignId) {
      return;
    }

    setStopLoading(true);
    setDetailError(null);
    try {
      const status = await campaignService.stopCampaign(selectedCampaignId);
      setSelectedCampaignStatus(status);
      syncCampaignInList(status);
      clearProgressTracking();
      setLiveStatus('disconnected');
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible detener la campaña webhook.';
      setDetailError(String(backendMessage));
    } finally {
      setStopLoading(false);
    }
  };

  const markWebhookConfigDraftDirty = () => {
    setWebhookConfigDirty(true);
    setWebhookConfigSuccess(null);
  };

  const handleWebhookConfigMediaSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setDetailError(null);
    setWebhookConfigSuccess(null);
    setWebhookConfigUploadingMedia(true);
    try {
      const upload = await campaignService.uploadCampaignHeaderMedia(file);
      markWebhookConfigDraftDirty();
      setWebhookConfigDraft((current) => {
        const base = current || {
          projectInterest: '',
          voiceModelSelection: '',
          skipIdentityValidation: false,
          scheduleReengagement: true,
          reengagementDelayBusinessDays: DEFAULT_REENGAGEMENT_DELAY_DAYS,
          noAnswerReengagementAttempts: DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS,
          noAnswerReengagementIncrementBusinessDays: DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS,
          headerMediaType: null,
          headerMediaId: '',
          headerMediaUrl: '',
          headerImageMediaId: '',
          headerImageUrl: '',
        };
        return {
          ...base,
          headerMediaType: upload.media_type,
          headerMediaId: upload.media_id,
          headerMediaUrl: upload.media_url,
          headerImageMediaId: upload.media_type === 'image' ? upload.media_id : '',
          headerImageUrl: upload.media_type === 'image' ? upload.media_url : '',
        };
      });
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar la media del encabezado del webhook.';
      setDetailError(String(backendMessage));
    } finally {
      setWebhookConfigUploadingMedia(false);
    }
  };

  const handleSaveWebhookConfig = async () => {
    if (!selectedCampaignId || !webhookConfigDraft) {
      return;
    }
    if (
      webhookConfigDraft.scheduleReengagement &&
      (!Number.isFinite(webhookConfigDraft.reengagementDelayBusinessDays) ||
        webhookConfigDraft.reengagementDelayBusinessDays <= 0 ||
        !Number.isInteger(webhookConfigDraft.noAnswerReengagementAttempts) ||
        webhookConfigDraft.noAnswerReengagementAttempts < 1 ||
        webhookConfigDraft.noAnswerReengagementAttempts > 10 ||
        !Number.isFinite(webhookConfigDraft.noAnswerReengagementIncrementBusinessDays) ||
        webhookConfigDraft.noAnswerReengagementIncrementBusinessDays <= 0)
    ) {
      setDetailError('Revisa el primer reenganche, el número de intentos (1 a 10) y su incremento.');
      return;
    }

    const payload: CampaignWebhookConfigUpdateRequest = {
      project_interest: webhookConfigDraft.projectInterest.trim() || null,
      voice_model_selection: webhookConfigDraft.voiceModelSelection || null,
      skip_identity_validation: webhookConfigDraft.skipIdentityValidation,
      schedule_reengagement: webhookConfigDraft.scheduleReengagement,
      reengagement_delay_business_days: webhookConfigDraft.scheduleReengagement
        ? webhookConfigDraft.reengagementDelayBusinessDays
        : null,
      no_answer_reengagement_attempts: webhookConfigDraft.noAnswerReengagementAttempts,
      no_answer_reengagement_increment_business_days:
        webhookConfigDraft.noAnswerReengagementIncrementBusinessDays,
      header_media_type: webhookConfigDraft.headerMediaType,
      header_media_id: webhookConfigDraft.headerMediaId.trim() || null,
      header_media_url: webhookConfigDraft.headerMediaUrl.trim() || null,
      header_image_media_id:
        webhookConfigDraft.headerMediaType === 'image'
          ? webhookConfigDraft.headerImageMediaId.trim() || webhookConfigDraft.headerMediaId.trim() || null
          : null,
      header_image_url:
        webhookConfigDraft.headerMediaType === 'image'
          ? webhookConfigDraft.headerImageUrl.trim() || webhookConfigDraft.headerMediaUrl.trim() || null
          : null,
    };

    setWebhookConfigSaving(true);
    setDetailError(null);
    setWebhookConfigSuccess(null);
    try {
      const status = await campaignService.updateWebhookCampaignConfig(selectedCampaignId, payload);
      const savedSignature = getWebhookConfigSignature(status);
      setSelectedCampaignStatus(status);
      syncCampaignInList(status);
      setWebhookConfigDraft(buildWebhookConfigDraft(status));
      setWebhookConfigDraftCampaignId(status.campaign_id);
      setWebhookConfigAppliedSignature(savedSignature);
      setWebhookConfigDirty(false);
      setWebhookConfigSuccess('Configuración del webhook actualizada.');
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible actualizar la configuración del webhook.';
      setDetailError(String(backendMessage));
    } finally {
      setWebhookConfigSaving(false);
    }
  };

  const startPollingFallback = (campaignId: string) => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
    }

    setLiveStatus('fallback-polling');
    pollTimerRef.current = window.setInterval(async () => {
      const status = await fetchSelectedCampaignStatus(campaignId);
      if (!status) {
        return;
      }

      if ((status.campaign_type || 'bulk') === 'webhook') {
        await fetchWebhookEvents(campaignId, recipientPageRef.current, recipientPageSizeRef.current);
      } else {
        await fetchRecipients(
          campaignId,
          recipientPageRef.current,
          recipientPageSizeRef.current,
          recipientStatusFilterRef.current
        );
      }

      if (TERMINAL_STATUSES.has((status.status || '').toLowerCase())) {
        if (pollTimerRef.current) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        setLiveStatus('disconnected');
      }
    }, 3000);
  };

  const streamSelectedCampaignProgress = async (campaignId: string) => {
    clearProgressTracking();
    setLiveStatus('connecting');

    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const streamUrl = `${apiBase}/campaigns/history/${campaignId}/stream?interval_ms=1200`;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const readSseStream = async (
      body: ReadableStream<Uint8Array>,
      onEvent: (eventName: string, data: string) => Promise<void>
    ) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');

          const lines = rawEvent.split(/\r?\n/);
          let eventName = 'message';
          const dataLines: string[] = [];

          for (const line of lines) {
            if (!line || line.startsWith(':')) {
              continue;
            }
            if (line.startsWith('event:')) {
              eventName = line.slice('event:'.length).trim() || 'message';
              continue;
            }
            if (line.startsWith('data:')) {
              dataLines.push(line.slice('data:'.length).trimStart());
            }
          }

          const data = dataLines.join('\n');
          if (data) {
            await onEvent(eventName, data);
          }
        }
      }
    };

    try {
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(streamUrl, {
        method: 'GET',
        headers,
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Campaign status stream failed (${response.status})`);
      }

      setLiveStatus('connected');
      await readSseStream(response.body, async (eventName, data) => {
        if (eventName !== 'campaign-progress') {
          return;
        }

        try {
          const payload = JSON.parse(data) as CampaignBulkStatusResponse;
          setSelectedCampaignStatus(payload);
          syncCampaignInList(payload);
          if ((payload.campaign_type || 'bulk') === 'webhook') {
            await fetchWebhookEvents(campaignId, recipientPageRef.current, recipientPageSizeRef.current);
          } else {
            await fetchRecipients(
              campaignId,
              recipientPageRef.current,
              recipientPageSizeRef.current,
              recipientStatusFilterRef.current
            );
          }
        } catch (error) {
          console.error('Failed to parse campaign-progress payload', error);
        }
      });

      const latest = await fetchSelectedCampaignStatus(campaignId);
      if (latest && !TERMINAL_STATUSES.has((latest.status || '').toLowerCase())) {
        startPollingFallback(campaignId);
      } else {
        setLiveStatus('disconnected');
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      console.warn('Campaign status stream disconnected; fallback to polling', error);
      startPollingFallback(campaignId);
    }
  };

  useEffect(() => {
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignPage, campaignPageSize, campaignStatusFilter]);

  useEffect(() => {
    if (!selectedCampaignId) {
      setSelectedCampaignStatus(null);
      setRecipients([]);
      setRecipientsTotal(0);
      setWebhookEvents([]);
      setWebhookEventsTotal(0);
      setDetailError(null);
      setWebhookConfigDraft(null);
      setWebhookConfigDraftCampaignId(null);
      setWebhookConfigAppliedSignature(null);
      setWebhookConfigDirty(false);
      setWebhookConfigSuccess(null);
      clearProgressTracking();
      setLiveStatus('idle');
      return;
    }

    let active = true;

    const bootstrap = async () => {
      setDetailError(null);
      const status = await fetchSelectedCampaignStatus(selectedCampaignId);
      if (!active) {
        return;
      }

      if (status?.campaign_type === 'webhook') {
        await fetchWebhookEvents(selectedCampaignId, recipientPageRef.current, recipientPageSizeRef.current);
      } else if (status?.campaign_type === 'bulk') {
        await fetchRecipients(
          selectedCampaignId,
          recipientPageRef.current,
          recipientPageSizeRef.current,
          recipientStatusFilterRef.current
        );
      }

      if (!status) {
        setLiveStatus('disconnected');
        return;
      }

      if (TERMINAL_STATUSES.has((status.status || '').toLowerCase())) {
        setLiveStatus('disconnected');
        return;
      }

      streamSelectedCampaignProgress(selectedCampaignId);
    };

    bootstrap();

    return () => {
      active = false;
      clearProgressTracking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignId || selectedCampaignType !== 'bulk') {
      return;
    }

    fetchRecipients(selectedCampaignId, recipientPage, recipientPageSize, recipientStatusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, recipientPage, recipientPageSize, recipientStatusFilter, selectedCampaignType]);

  useEffect(() => {
    if (!selectedCampaignId || selectedCampaignType !== 'webhook') {
      return;
    }

    fetchWebhookEvents(selectedCampaignId, recipientPage, recipientPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, recipientPage, recipientPageSize, selectedCampaignType]);

  const campaignColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: 'Creada',
        minWidth: 170,
        flex: 0.8,
        valueFormatter: (params) => formatDateTime(params.value as string),
      },
      { field: 'campaign_id', headerName: 'ID Campaña', minWidth: 220, flex: 1.2 },
      {
        field: 'campaign_type',
        headerName: 'Tipo',
        minWidth: 120,
        flex: 0.5,
        valueGetter: (params) => ((params.row.campaign_type || 'bulk') === 'webhook' ? 'Webhook' : 'Masiva'),
      },
      {
        field: 'campaign_name',
        headerName: 'Nombre',
        minWidth: 170,
        flex: 0.9,
        valueGetter: (params) => params.row.campaign_name || '-',
      },
      { field: 'template_name', headerName: 'Plantilla', minWidth: 170, flex: 0.9 },
      {
        field: 'status',
        headerName: 'Estado',
        minWidth: 130,
        flex: 0.6,
        renderCell: (params) => <CampaignStatusChip status={String(params.value || '')} />,
      },
      {
        field: 'progress_percent',
        headerName: 'Progreso',
        minWidth: 120,
        flex: 0.6,
        valueGetter: (params) =>
          (params.row.campaign_type || 'bulk') === 'webhook'
            ? (params.row.is_live ? 'En vivo' : 'Detenida')
            : `${Number(params.row.progress_percent || 0).toFixed(1)}%`,
      },
      {
        field: 'processing_rows',
        headerName: 'Procesando',
        minWidth: 120,
        flex: 0.55,
        valueGetter: (params) =>
          (params.row.campaign_type || 'bulk') === 'webhook'
            ? Number(params.row.total_triggers || 0)
            : Number(params.row.processing_rows || 0),
      },
      { field: 'delivered_rows', headerName: 'Recibidos', minWidth: 120, flex: 0.55 },
      { field: 'read_rows', headerName: 'Leídos', minWidth: 110, flex: 0.5 },
      {
        field: 'processed_rows',
        headerName: 'Procesados',
        minWidth: 145,
        flex: 0.7,
        valueGetter: (params) =>
          (params.row.campaign_type || 'bulk') === 'webhook'
            ? `${params.row.successful_triggers || 0}/${params.row.total_triggers || 0}`
            : `${params.row.processed_rows}/${params.row.valid_rows}`,
      },
      { field: 'sent_rows', headerName: 'Enviados', minWidth: 110, flex: 0.5 },
      { field: 'failed_rows', headerName: 'Fallidos', minWidth: 110, flex: 0.5 },
    ],
    []
  );

  const recipientColumns: GridColDef[] = useMemo(
    () => [
      { field: 'row_number', headerName: 'Fila', width: 90 },
      { field: 'phone_number', headerName: 'Número', minWidth: 170, flex: 0.8 },
      {
        field: 'status',
        headerName: 'Estado',
        minWidth: 220,
        flex: 1.1,
        sortable: false,
        renderCell: (params) => <DeliveryStatusChip item={params.row as CampaignBulkRecipient} />,
      },
      { field: 'whatsapp_message_id', headerName: 'WhatsApp ID', minWidth: 220, flex: 1 },
      {
        field: 'sent_at',
        headerName: 'Enviado',
        minWidth: 170,
        flex: 0.8,
        valueFormatter: (params) => formatDateTime((params.value as string) || null),
      },
      {
        field: 'next_retry_at',
        headerName: 'Próximo reintento',
        minWidth: 180,
        flex: 0.9,
        valueFormatter: (params) => formatDateTime((params.value as string) || null),
      },
      {
        field: 'error_message',
        headerName: 'Detalle',
        minWidth: 320,
        flex: 1.3,
        valueGetter: (params) => getRecipientDetailText(params.row as CampaignBulkRecipient),
      },
      {
        field: 'warnings',
        headerName: 'Advertencias',
        minWidth: 320,
        flex: 1.3,
        valueGetter: (params) =>
          ((params.row as CampaignBulkRecipient).warnings || []).join(' '),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        minWidth: 150,
        flex: 0.7,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const recipient = params.row as CampaignBulkRecipient;
          const isRetrying = retryingRecipientId === recipient.id;
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedCampaignId || retryingRecipientId !== null}
              onClick={(event) => {
                event.stopPropagation();
                void handleRetryRecipient(recipient);
              }}
            >
              {isRetrying ? 'Reintentando...' : 'Reintentar'}
            </Button>
          );
        },
      },
    ],
    [handleRetryRecipient, retryingRecipientId, selectedCampaignId]
  );

  const webhookEventColumns: GridColDef[] = useMemo(
    () => [
      { field: 'trigger_number', headerName: 'Disparo', width: 100 },
      { field: 'phone_number', headerName: 'Número', minWidth: 170, flex: 0.8 },
      { field: 'customer_name', headerName: 'Nombre', minWidth: 180, flex: 0.8 },
      {
        field: 'status',
        headerName: 'Estado',
        minWidth: 220,
        flex: 1.1,
        sortable: false,
        renderCell: (params) => <DeliveryStatusChip item={params.row as CampaignWebhookEvent} />,
      },
      { field: 'whatsapp_message_id', headerName: 'WhatsApp ID', minWidth: 220, flex: 1 },
      {
        field: 'created_at',
        headerName: 'Recibido',
        minWidth: 170,
        flex: 0.8,
        valueFormatter: (params) => formatDateTime((params.value as string) || null),
      },
      {
        field: 'error_message',
        headerName: 'Detalle',
        minWidth: 320,
        flex: 1.3,
        valueGetter: (params) => getRecipientDetailText(params.row as CampaignWebhookEvent),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        minWidth: 150,
        flex: 0.7,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const eventRow = params.row as CampaignWebhookEvent;
          const isRetrying = retryingWebhookEventId === eventRow.id;
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedCampaignId || retryingWebhookEventId !== null}
              onClick={(event) => {
                event.stopPropagation();
                void handleRetryWebhookEvent(eventRow);
              }}
            >
              {isRetrying ? 'Reintentando...' : 'Reintentar'}
            </Button>
          );
        },
      },
    ],
    [handleRetryWebhookEvent, retryingWebhookEventId, selectedCampaignId]
  );

  const listStatusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'queued', label: 'En cola' },
    { value: 'running', label: 'En ejecución' },
    { value: 'live', label: 'En vivo' },
    { value: 'stopped', label: 'Detenidas' },
    { value: 'completed', label: 'Completadas' },
    { value: 'failed', label: 'Fallidas' },
    { value: 'cancelled', label: 'Canceladas' },
  ];

  const recipientStatusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'processing', label: 'Procesando' },
    { value: 'sent', label: 'Enviados' },
    { value: 'delivered', label: 'Recibidos' },
    { value: 'read', label: 'Leídos' },
    { value: 'retry_scheduled', label: 'Reintentos' },
    { value: 'retry_exhausted', label: 'Agotados' },
    { value: 'failed', label: 'Fallidos' },
    { value: 'invalid', label: 'Inválidos' },
  ];

  return (
    <Box className="fade-in">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Historial de Campañas
        </Typography>
        {hasPermission('export_campaign_cost_reports') && (
          <Button variant="contained" href="/campaigns/cost-reports">
            Reporte completo de costos
          </Button>
        )}
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Consulta campañas masivas y webhook, su estado y el detalle de entregas o disparos.
      </Typography>

      <Stack spacing={2.5}>
        <Paper sx={{ p: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="campaign-history-status-filter-label">Estado</InputLabel>
              <Select
                labelId="campaign-history-status-filter-label"
                value={campaignStatusFilter}
                label="Estado"
                onChange={(event) => {
                  setCampaignStatusFilter(String(event.target.value));
                  setCampaignPage(0);
                }}
              >
                {listStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button variant="outlined" onClick={loadCampaigns} disabled={campaignsLoading}>
              {campaignsLoading ? 'Actualizando...' : 'Actualizar listado'}
            </Button>
          </Stack>

          {listError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {listError}
            </Alert>
          )}

          <Box sx={{ height: 420, width: '100%', mt: 2 }}>
            <DataGrid
              rows={campaigns}
              columns={campaignColumns}
              getRowId={(row) => row.campaign_id}
              loading={campaignsLoading}
              rowCount={campaignsTotal}
              pageSize={campaignPageSize}
              rowsPerPageOptions={[25, 50, 100]}
              paginationMode="server"
              page={campaignPage}
              onPageChange={(newPage) => setCampaignPage(newPage)}
              onPageSizeChange={(newSize) => {
                setCampaignPageSize(newSize);
                setCampaignPage(0);
              }}
              onRowClick={(params) => {
                const campaignId = String(params.row.campaign_id || '');
                if (!campaignId) {
                  return;
                }
                setSelectedCampaignId(campaignId);
                setSelectedCampaignStatus(params.row as CampaignBulkStatusResponse);
                setRecipients([]);
                setRecipientsTotal(0);
                setWebhookEvents([]);
                setWebhookEventsTotal(0);
                setDetailError(null);
                setWebhookConfigSuccess(null);
                setRecipientPage(0);
              }}
              disableSelectionOnClick
              pagination
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Typography variant="h6">Detalle de campaña</Typography>
              {selectedCampaignStatus && <CampaignStatusChip status={selectedCampaignStatus.status} />}
              {selectedCampaignStatus && (
                <Chip
                  size="small"
                  label={
                    liveStatus === 'connected'
                      ? 'Actualización en vivo'
                      : liveStatus === 'fallback-polling'
                      ? 'Fallback por sondeo'
                      : liveStatus === 'connecting'
                      ? 'Conectando...'
                      : liveStatus === 'idle'
                      ? 'Sin seguimiento activo'
                      : 'Sin conexión'
                  }
                />
              )}

              <Box sx={{ flexGrow: 1 }} />

              <Button
                variant="text"
                size="small"
                disabled={!selectedCampaignId}
                onClick={async () => {
                  if (!selectedCampaignId) {
                    return;
                  }
                  const status = await fetchSelectedCampaignStatus(selectedCampaignId);
                  if (status?.campaign_type === 'webhook') {
                    await fetchWebhookEvents(selectedCampaignId, recipientPageRef.current, recipientPageSizeRef.current);
                  } else if (status?.campaign_type === 'bulk') {
                    await fetchRecipients(
                      selectedCampaignId,
                      recipientPageRef.current,
                      recipientPageSizeRef.current,
                      recipientStatusFilterRef.current
                    );
                  }
                }}
              >
                Actualizar detalle
              </Button>
            </Stack>

            {detailError && <Alert severity="error">{detailError}</Alert>}

            {!selectedCampaignId && (
              <Alert severity="info">Selecciona una campaña del listado para ver su detalle y destinatarios.</Alert>
            )}

            {selectedCampaignStatus && (
              <Stack spacing={2}>
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo:</strong> {(selectedCampaignStatus.campaign_type || 'bulk') === 'webhook' ? 'Webhook' : 'Masiva'}
                  </Typography>
                  {selectedCampaignStatus.campaign_name && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Nombre:</strong> {selectedCampaignStatus.campaign_name}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {selectedCampaignStatus.campaign_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Plantilla:</strong> {selectedCampaignStatus.template_name}
                  </Typography>
                  {(selectedCampaignStatus.campaign_type || 'bulk') === 'webhook' ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Endpoint:</strong> {selectedCampaignStatus.webhook_endpoint_url || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Variables runtime:</strong>{' '}
                        {(selectedCampaignStatus.runtime_variable_names || []).length
                          ? (selectedCampaignStatus.runtime_variable_names || []).join(', ')
                          : '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Último disparo:</strong> {formatDateTime(selectedCampaignStatus.last_triggered_at)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Archivo:</strong> {selectedCampaignStatus.source_filename || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Borrado de memoria de IA:</strong>{' '}
                        {selectedCampaignStatus.reset_conversation_memory
                          ? 'Activado'
                          : 'Desactivado'}
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Creada:</strong> {formatDateTime(selectedCampaignStatus.created_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Inicio:</strong> {formatDateTime(selectedCampaignStatus.started_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fin:</strong> {formatDateTime(selectedCampaignStatus.completed_at)}
                  </Typography>
                </Stack>

                {(selectedCampaignStatus.campaign_type || 'bulk') === 'webhook' ? (
                  <Alert severity={selectedCampaignStatus.is_live ? 'success' : 'info'}>
                    {selectedCampaignStatus.is_live
                      ? 'Webhook activo y listo para recibir disparos.'
                      : `Webhook detenido${selectedCampaignStatus.stopped_at ? ` el ${formatDateTime(selectedCampaignStatus.stopped_at)}` : ''}.`}
                  </Alert>
                ) : (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, selectedCampaignStatus.progress_percent || 0))}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {Number(selectedCampaignStatus.progress_percent || 0).toFixed(2)}% completado ({selectedCampaignStatus.processed_rows}/
                      {selectedCampaignStatus.valid_rows} válidos procesados)
                    </Typography>
                  </>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
                  <Chip
                    color="info"
                    label={
                      (selectedCampaignStatus.campaign_type || 'bulk') === 'webhook'
                        ? `Disparos: ${selectedCampaignStatus.total_triggers || 0}`
                        : `Total: ${selectedCampaignStatus.total_rows}`
                    }
                  />
                  <Chip
                    color="default"
                    label={
                      (selectedCampaignStatus.campaign_type || 'bulk') === 'webhook'
                        ? `Exitosos: ${selectedCampaignStatus.successful_triggers || 0}`
                        : `Procesando: ${selectedCampaignStatus.processing_rows}`
                    }
                  />
                  <Chip color="info" label={`Enviados: ${selectedCampaignStatus.sent_rows}`} />
                  <Chip color="success" label={`Recibidos: ${selectedCampaignStatus.delivered_rows}`} />
                  <Chip color="success" label={`Leídos: ${selectedCampaignStatus.read_rows}`} />
                  <Chip color="warning" label={`Reintentos: ${selectedCampaignStatus.retry_scheduled_rows}`} />
                  <Chip color="error" label={`Fallidos: ${selectedCampaignStatus.failed_rows}`} />
                  <Chip
                    color="warning"
                    label={
                      (selectedCampaignStatus.campaign_type || 'bulk') === 'webhook'
                        ? `Fallidos trigger: ${selectedCampaignStatus.failed_triggers || 0}`
                        : `Pendientes: ${selectedCampaignStatus.pending_rows}`
                    }
                  />
                  <Chip label={`Opt-out: ${selectedCampaignStatus.opted_out_rows}`} />
                  <Chip label={`No entregables: ${selectedCampaignStatus.unreachable_rows}`} />
                  {(selectedCampaignStatus.campaign_type || 'bulk') !== 'webhook' && (
                    <Chip label={`Inválidos/Omitidos: ${selectedCampaignStatus.skipped_rows}`} />
                  )}
                </Stack>

                {(selectedCampaignStatus.campaign_type || 'bulk') === 'webhook' && webhookConfigDraft && (
                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 2,
                    }}
                  >
                    <Stack spacing={1.75}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <Typography variant="subtitle1">Configuración del webhook</Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        {webhookConfigUploadingMedia && <CircularProgress size={20} />}
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={webhookConfigSaving || webhookConfigUploadingMedia}
                        >
                          {webhookConfigUploadingMedia ? 'Cargando media...' : 'Cambiar media'}
                          <input
                            hidden
                            type="file"
                            accept="image/jpeg,image/png,image/webp,video/mp4"
                            onChange={handleWebhookConfigMediaSelection}
                          />
                        </Button>
                        <Button
                          variant="contained"
                          disabled={webhookConfigSaving || webhookConfigUploadingMedia}
                          onClick={handleSaveWebhookConfig}
                        >
                          {webhookConfigSaving ? 'Guardando...' : 'Guardar configuración'}
                        </Button>
                      </Stack>

                      {webhookConfigSuccess && <Alert severity="success">{webhookConfigSuccess}</Alert>}

                      <TextField
                        select
                        label="Voz TTS"
                        value={webhookConfigDraft.voiceModelSelection}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(event) => {
                          markWebhookConfigDraftDirty();
                          setWebhookConfigDraft((current) =>
                            current ? { ...current, voiceModelSelection: event.target.value } : current
                          );
                        }}
                        disabled={webhookConfigSaving}
                        fullWidth
                        helperText="Se aplica a contactos nuevos y existentes en futuros disparos y reintentos manuales. La voz enviada por HubSpot tiene prioridad. Predeterminada conserva la voz de contactos existentes."
                      >
                        {VOICE_MODEL_OPTIONS.map((option) => (
                          <MenuItem key={option.value || 'default'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Tipo de media"
                          value={webhookConfigDraft.headerMediaType || 'Sin media'}
                          size="small"
                          InputProps={{ readOnly: true }}
                          sx={{ minWidth: 180 }}
                        />
                        <TextField
                          label="WhatsApp media ID"
                          value={webhookConfigDraft.headerMediaId || '-'}
                          size="small"
                          InputProps={{ readOnly: true }}
                          fullWidth
                        />
                      </Stack>

                      <TextField
                        label="URL pública de media"
                        value={webhookConfigDraft.headerMediaUrl || '-'}
                        size="small"
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />

                      <Autocomplete
                        options={projectOptions}
                        value={selectedWebhookProject}
                        loading={projectsLoading}
                        getOptionLabel={getProjectOptionLabel}
                        isOptionEqualToValue={(option, selected) => option.project_code === selected.project_code}
                        filterOptions={(options) => options}
                        onInputChange={(_, nextInput) => setProjectSearchInput(nextInput)}
                        onChange={(_, nextValue) => {
                          const nextProject = nextValue?.project_code || '';
                          if (webhookConfigDraft.projectInterest === nextProject) {
                            return;
                          }
                          markWebhookConfigDraftDirty();
                          setWebhookConfigDraft({ ...webhookConfigDraft, projectInterest: nextProject });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Proyecto de interés"
                            size="small"
                            helperText="Se aplicará como contexto inicial para futuros disparos."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ minWidth: 260 }}>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={webhookConfigDraft.skipIdentityValidation}
                                onChange={(event) => {
                                  markWebhookConfigDraftDirty();
                                  setWebhookConfigDraft((current) =>
                                    current
                                      ? { ...current, skipIdentityValidation: event.target.checked }
                                      : current
                                  );
                                }}
                                color="primary"
                              />
                            )}
                            label="Omitir validación de identidad"
                          />
                        </Box>

                        <Box sx={{ minWidth: 260 }}>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={webhookConfigDraft.scheduleReengagement}
                                onChange={(event) => {
                                  markWebhookConfigDraftDirty();
                                  setWebhookConfigDraft((current) =>
                                    current
                                      ? { ...current, scheduleReengagement: event.target.checked }
                                      : current
                                  );
                                }}
                                color="primary"
                              />
                            )}
                            label="Programar reenganche"
                          />
                          <TextField
                            label="Primer reenganche (días hábiles)"
                            type="number"
                            size="small"
                            value={webhookConfigDraft.reengagementDelayBusinessDays}
                            onChange={(event) => {
                              markWebhookConfigDraftDirty();
                              setWebhookConfigDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      reengagementDelayBusinessDays: normalizeReengagementDelayInput(event.target.value),
                                    }
                                  : current
                              );
                            }}
                            inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                            disabled={!webhookConfigDraft.scheduleReengagement}
                            sx={{ mt: 1, maxWidth: 260 }}
                          />
                          <TextField
                            label="Reenganches sin respuesta"
                            type="number"
                            size="small"
                            value={webhookConfigDraft.noAnswerReengagementAttempts}
                            onChange={(event) => {
                              markWebhookConfigDraftDirty();
                              setWebhookConfigDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      noAnswerReengagementAttempts: normalizeNoAnswerAttemptsInput(event.target.value),
                                    }
                                  : current
                              );
                            }}
                            inputProps={{ min: 1, max: 10, step: 1 }}
                            disabled={!webhookConfigDraft.scheduleReengagement}
                            sx={{ mt: 1, maxWidth: 260 }}
                            helperText="Incluye el intento final."
                          />
                          <TextField
                            label="Incremento por intento (días hábiles)"
                            type="number"
                            size="small"
                            value={webhookConfigDraft.noAnswerReengagementIncrementBusinessDays}
                            onChange={(event) => {
                              markWebhookConfigDraftDirty();
                              setWebhookConfigDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      noAnswerReengagementIncrementBusinessDays: normalizeReengagementDelayInput(event.target.value),
                                    }
                                  : current
                              );
                            }}
                            inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                            disabled={!webhookConfigDraft.scheduleReengagement}
                            sx={{ mt: 1, maxWidth: 260 }}
                          />
                        </Box>
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        Los cambios se aplican a futuros disparos de este mismo webhook y a reintentos manuales posteriores.
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {(selectedCampaignStatus.campaign_type || 'bulk') !== 'webhook' && (
                  <Stack spacing={1.25}>
                    <ProgressRow
                      label="Recibidos por el cliente"
                      value={selectedCampaignStatus.delivered_rows}
                      total={selectedCampaignStatus.valid_rows}
                      color="#2e7d32"
                    />
                    <ProgressRow
                      label="Leídos por el cliente"
                      value={selectedCampaignStatus.read_rows}
                      total={selectedCampaignStatus.valid_rows}
                      color="#1565c0"
                    />
                    <ProgressRow
                      label="Pendientes de reintento"
                      value={selectedCampaignStatus.retry_scheduled_rows}
                      total={selectedCampaignStatus.valid_rows}
                      color="#ed6c02"
                    />
                    <ProgressRow
                      label="No se pudo entregar"
                      value={selectedCampaignStatus.failed_rows}
                      total={selectedCampaignStatus.valid_rows}
                      color="#d32f2f"
                    />
                    <ProgressRow
                      label="Procesando"
                      value={selectedCampaignStatus.processing_rows}
                      total={selectedCampaignStatus.valid_rows}
                      color="#6b7280"
                    />
                  </Stack>
                )}

                {selectedCampaignStatus.error_message && (
                  <Alert severity="error">{selectedCampaignStatus.error_message}</Alert>
                )}

                {(selectedCampaignStatus.campaign_type || 'bulk') === 'webhook' ? (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                      <Typography variant="subtitle1">Eventos del webhook</Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={!selectedCampaignStatus.is_live || stopLoading}
                        onClick={handleStopCampaign}
                      >
                        {stopLoading ? 'Deteniendo...' : 'Detener webhook'}
                      </Button>
                      {webhookEventsLoading && <CircularProgress size={20} />}
                    </Stack>

                    <Box sx={{ height: 420, width: '100%' }}>
                      <DataGrid
                        rows={webhookEvents}
                        columns={webhookEventColumns}
                        getRowId={(row) => row.id}
                        loading={webhookEventsLoading}
                        rowCount={webhookEventsTotal}
                        pageSize={recipientPageSize}
                        rowsPerPageOptions={[25, 50, 100]}
                        paginationMode="server"
                        page={recipientPage}
                        onPageChange={(newPage) => setRecipientPage(newPage)}
                        onPageSizeChange={(newSize) => {
                          setRecipientPageSize(newSize);
                          setRecipientPage(0);
                        }}
                        disableSelectionOnClick
                        pagination
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel id="campaign-recipient-status-filter-label">Estado destinatario</InputLabel>
                        <Select
                          labelId="campaign-recipient-status-filter-label"
                          value={recipientStatusFilter}
                          label="Estado destinatario"
                          onChange={(event) => {
                            setRecipientStatusFilter(String(event.target.value));
                            setRecipientPage(0);
                          }}
                        >
                          {recipientStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ flexGrow: 1 }} />

                      <Button
                        variant="outlined"
                        disabled={!selectedCampaignId || exportLoading}
                        onClick={handleExportRecipients}
                      >
                        {exportLoading ? 'Exportando...' : 'Exportar Excel'}
                      </Button>

                      {recipientsLoading && <CircularProgress size={20} />}
                    </Stack>

                    <Box sx={{ height: 420, width: '100%' }}>
                      <DataGrid
                        rows={recipients}
                        columns={recipientColumns}
                        getRowId={(row) => row.id}
                        loading={recipientsLoading}
                        rowCount={recipientsTotal}
                        pageSize={recipientPageSize}
                        rowsPerPageOptions={[25, 50, 100]}
                        paginationMode="server"
                        page={recipientPage}
                        onPageChange={(newPage) => setRecipientPage(newPage)}
                        onPageSizeChange={(newSize) => {
                          setRecipientPageSize(newSize);
                          setRecipientPage(0);
                        }}
                        disableSelectionOnClick
                        pagination
                      />
                    </Box>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default CampaignHistory;
